// CategoryMatrixPreview.jsx
//
// Matrix Mode illustration of ONE category: its best-selling products
// (computed server-side, see server_elakay
// elakay_services/categoryMatrixPreview.js) composed as ONE image. Same
// visual language as the category matrix cards of /product/cat/:id
// (swiperCategoriesMatrix.jsx: minmax(0,1fr) tracks, cover images, title
// overlaid on a bottom gradient). Only real product images are shown --
// never a duplicated product nor an empty grey cell:
//   4 -> 2x2 · 3 -> one tall + two stacked · 2 -> side by side · 1 -> full
//   0 -> the category's own image (fallbackSrc), else the Elakay empty icon.
import React from "react";
import ElakayImage from "../../components/images/ElakayImage";
import { toImageUrl } from "../../config";
import elakayEmptyBox from "../Layout/icon/elakayEmptyBox.png";
import imgProductLakay from "../Layout/imgs/md3.jpg";
import "./CategoryMatrixPreview.css";

export const MATRIX_SLOTS = 4;

// Up to MATRIX_SLOTS distinct products that have an image URL, best first.
export const buildMatrixImages = (products) => {
    const seen = new Set();
    return (Array.isArray(products) ? products : []).filter((product) => {
        const key = String(product?._id || product?.imageUrl || "");
        if (!product?.imageUrl || seen.has(key)) return false;
        seen.add(key);
        return true;
    }).slice(0, MATRIX_SLOTS);
};

// Matrix Mode lookup shared by the All Categories cards: `matrixParents` is
// the previews response's `parents` map. Returns the parent's children map
// when the parent has Matrix Mode ON, else null (= default mode).
export const getMatrixChildren = (matrixParents, parentId) => {
    const parent = parentId ? matrixParents?.[String(parentId)] : null;
    return parent?.matrixMode ? (parent.children || {}) : null;
};

// title: shown INSIDE the composition, over a bottom gradient (the
// sub-category card); the caller then renders no second title below it.
const CategoryMatrixPreview = ({ products, className = "", label = "", title = "", fallbackSrc = "" }) => {
    const images = buildMatrixImages(products);
    return (
        <div
            className={`ec-matrix ec-matrix--count-${images.length}${title ? " ec-matrix--titled" : ""} ${className}`.trim()}
            data-testid="category-matrix"
            aria-label={label || title || undefined}
        >
            {images.map((product, index) => (
                <div key={String(product._id || index)} className="ec-matrix__cell">
                    <ElakayImage
                        preset="category"
                        sizes={images.length > 1 ? "(max-width: 767px) 25vw, 160px" : "(max-width: 767px) 50vw, 320px"}
                        fallback={imgProductLakay}
                        className="ec-matrix__img"
                        src={toImageUrl(product.imageUrl)}
                        alt={product.name || ""}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            ))}
            {!images.length && (
                fallbackSrc ? (
                    <div className="ec-matrix__cell ec-matrix__cell--fallback">
                        <ElakayImage
                            preset="category"
                            fallback={imgProductLakay}
                            className="ec-matrix__img"
                            src={fallbackSrc}
                            alt={label || title || ""}
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                ) : (
                    <div className="ec-matrix__cell ec-matrix__cell--empty" aria-hidden="true">
                        <img className="ec-matrix__empty-icon" src={elakayEmptyBox} alt="" />
                    </div>
                )
            )}
            {title && (
                <div className="ec-matrix__overlay">
                    <span className="ec-matrix__title">{title}</span>
                </div>
            )}
        </div>
    );
};

export default CategoryMatrixPreview;
